/**
 * GGJ brand logo components — theme-adaptive assets from public/social-logos.
 *
 * Exports:
 *   ThemeLogoImg   — low-level light/dark image swap (html.dark via Tailwind)
 *   GGJLogoFull    — mark + "GoGrabJob" wordmark
 *   GGJLogoMark    — square mark only
 *   GGJLogoLockup  — horizontal lockup PNG (mark + wordmark baked in)
 *   GGJLogoStacked — stacked mark + "GGJ" PNG
 */
import type { CSSProperties } from 'react';
import { BRAND_LOGOS, type BrandLogoPair } from '../../config/brandLogos';
import { useTheme } from '../../contexts/ThemeContext';

interface ThemeLogoImgProps {
  src: BrandLogoPair;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  imgClassName?: string;
  style?: CSSProperties;
  draggable?: boolean;
  forceVariant?: 'light' | 'dark';
}

export function ThemeLogoImg({
  src,
  alt,
  width,
  height,
  className = '',
  imgClassName = '',
  style,
  draggable = false,
  /** When set, bypasses app theme (e.g. always purple on a light marketing surface). */
  forceVariant,
}: ThemeLogoImgProps) {
  const { theme } = useTheme();
  const variant = forceVariant ?? theme;
  const imgStyle: CSSProperties = {
    display: 'block',
    width: width ?? '100%',
    height: height ?? '100%',
    objectFit: 'contain',
    ...style,
  };

  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center ${className}`}
      style={width && height ? { width, height } : undefined}
    >
      <img
        src={variant === 'dark' ? src.dark : src.light}
        alt={alt}
        width={width}
        height={height}
        draggable={draggable}
        className={`block max-h-full max-w-full ${imgClassName}`}
        style={imgStyle}
      />
    </span>
  );
}

/* ─── GGJLogoMark ──────────────────────────────────────────────────────────── */

interface GGJLogoMarkProps {
  size?: number;
  className?: string;
  /** Compensates for transparent padding baked into mark PNGs. */
  boostVisual?: boolean;
  /**
   * `auto` — theme-swapped mark from public/social-logos/light|dark (default).
   * `light` — always use the light-folder mark (fixed light backgrounds only).
   */
  surface?: 'auto' | 'light';
}

export function GGJLogoMark({
  size = 34,
  className = '',
  boostVisual = true,
  surface = 'auto',
}: GGJLogoMarkProps) {
  const markClass = `ggj-logo-mark${boostVisual ? ' ggj-logo-mark--boost' : ''} ${className}`.trim();

  if (surface === 'light') {
    return (
      <span
        className={`${markClass} inline-flex shrink-0 items-center justify-center`}
        style={{ width: size, height: size }}
      >
        <img
          src={BRAND_LOGOS.mark.light}
          alt="GoGrabJob"
          width={size}
          height={size}
          draggable={false}
          className="ggj-logo-mark__img block max-h-full max-w-full object-contain"
          style={{ display: 'block', width: size, height: size, objectFit: 'contain' }}
        />
      </span>
    );
  }

  return (
    <ThemeLogoImg
      src={BRAND_LOGOS.mark}
      alt="GoGrabJob"
      width={size}
      height={size}
      className={markClass}
      imgClassName="ggj-logo-mark__img"
      draggable={false}
    />
  );
}

/* ─── GGJLogoFull ──────────────────────────────────────────────────────────── */

interface GGJLogoFullProps {
  iconSize?: number;
  fontSize?: number;
  textColor?: string;
  className?: string;
  markSurface?: 'auto' | 'light';
  boostMark?: boolean;
}

export function GGJLogoFull({
  iconSize = 34,
  fontSize = 15,
  textColor,
  className = '',
  markSurface = 'auto',
  boostMark = true,
}: GGJLogoFullProps) {
  const hasExplicit = Boolean(textColor);
  const textDefaultClass = hasExplicit ? '' : 'text-gray-900 dark:text-white';

  return (
    <div className={`ggj-logo-full flex items-center gap-2.5 ${className}`}>
      <span
        className="ggj-logo-full__icon inline-flex shrink-0 items-center justify-center"
        style={{ width: iconSize, height: iconSize }}
      >
        <GGJLogoMark size={iconSize} surface={markSurface} boostVisual={boostMark} />
      </span>
      <span
        className={`ggj-logo-full__label min-w-0 truncate font-bold whitespace-nowrap tracking-tight ${textDefaultClass}`}
        style={
          hasExplicit && textColor
            ? { fontSize, color: textColor, lineHeight: 1 }
            : { fontSize, lineHeight: 1 }
        }
      >
        GoGrabJob
      </span>
    </div>
  );
}

/* ─── GGJLogoLockup ────────────────────────────────────────────────────────── */

interface GGJLogoLockupProps {
  height?: number;
  className?: string;
}

export function GGJLogoLockup({ height = 34, className = '' }: GGJLogoLockupProps) {
  const { theme } = useTheme();
  return (
    <span className={`inline-flex shrink-0 items-center ${className}`} style={{ height }}>
      <img
        src={theme === 'dark' ? BRAND_LOGOS.lockup.dark : BRAND_LOGOS.lockup.light}
        alt="GoGrabJob"
        className="block h-full w-auto"
        draggable={false}
      />
    </span>
  );
}

/* ─── GGJLogoStacked ───────────────────────────────────────────────────────── */

interface GGJLogoStackedProps {
  height?: number;
  className?: string;
}

export function GGJLogoStacked({ height = 72, className = '' }: GGJLogoStackedProps) {
  const { theme } = useTheme();
  return (
    <span
      className={`ggj-logo-pill inline-flex shrink-0 items-center ${className}`}
      style={{ height }}
    >
      <img
        src={theme === 'dark' ? BRAND_LOGOS.stacked.dark : BRAND_LOGOS.stacked.light}
        alt="GoGrabJob"
        className="block h-full w-auto"
        draggable={false}
      />
    </span>
  );
}
